import React, { useState, useEffect, useRef } from 'react'
import Modal from 'aldoo-ra/Modal'
import Button from 'aldoo-ra/Button'
import Typography from 'aldoo-ra/Typography'
import {
  XMarkIcon,
  PlusCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline' // Plus, X, and Help icons
import Tooltip from 'aldoo-ra/Tooltip' // Assuming you have a Tooltip component
import Localization from '.'

const LanguageAddDialog = ({ isOpen, onClose, controller }) => {
  const [languages, setLanguages] = useState([
    {
      code: '',
      name: '',
      displayName: '',
      error: { code: false, name: false, displayName: false },
    },
  ])

  const [checkForErrors, setCheckForErrors] = useState(false)

  const newInputRef = useRef(null) // Reference for focusing the newly added "Language Code" input

  const {
    languages: availableLanguages,
    refreshLanguages: refreshLocalizationLanguages,
  } = Localization()

  useEffect(() => {
    if (isOpen) {
      // Refresh the available languages when the modal is opened
      refreshLocalizationLanguages()

      // Clear the first language item when modal is opened
      setLanguages([
        {
          code: '',
          name: '',
          displayName: '',
          error: { code: false, name: false, displayName: false },
        },
      ])
      // Focus on the first "Language Code" input
      if (newInputRef.current) {
        newInputRef.current.focus()
      }

      setCheckForErrors(false)
    }
  }, [isOpen])

  const hasError = (language, field) => {
    return language.error[field]
  }

  const languageExistError = (language, field) => {
    return language.error[field] === 'exists'
  }

  // Handle adding a new language row
  const handleAddNewLanguage = () => {
    const newLanguage = {
      code: '',
      name: '',
      displayName: '',
      error: { code: false, name: false, displayName: false },
    }
    setLanguages([newLanguage, ...languages]) // Add the new item to the top
    setTimeout(() => {
      if (newInputRef.current) {
        newInputRef.current.focus() // Focus on the newly added language code input
      }
    }, 100) // Timeout to allow the DOM to update
  }

  // Handle removing a language row by index
  const handleRemoveLanguage = (index) => {
    if (languages.length > 1) {
      const updatedLanguages = languages.filter((_, i) => i !== index)
      setLanguages(updatedLanguages)
    }
  }

  // Handle changing the values in the language fields and reset error if any
  const handleLanguageChange = (index, field, value) => {
    const updatedLanguages = [...languages]
    updatedLanguages[index][field] = value
    setLanguages(updatedLanguages)

    //delete language error
    delete languages[index].error[field]
    setCheckForErrors(false)
  }

  const checkForFieldError = (languages, field, onErrorValue) => {
    languages.forEach((language, index) => {
      if (
        availableLanguages.find((item) => item[field] === language[field]) &&
        !language.error[field]
      ) {
        language.error[field] = onErrorValue
      }
    })
  }

  // Validation before saving
  const validateLanguages = (languages) => {
    setCheckForErrors(true)
    //check for errors and return false if any
    //also fill the errors so we can display them
    languages.map((language) => {
      language.error = {
        code: !language.code,
        name: !language.name,
        displayName: !language.displayName,
      }
    })

    //check if the current language code is not in the available languages
    checkForFieldError(languages, 'code', 'exists')
    checkForFieldError(languages, 'name', 'exists')
    checkForFieldError(languages, 'displayName', 'exists')

    return !languages.some((language) =>
      Object.values(language.error).some((error) => error),
    )
  }

  const getErrorNotification = () => {
    const errors = languages.reduce((acc, language) => {
      Object.entries(language.error).forEach(([key, value]) => {
        if (value) {
          acc.push({ [key]: value })
        }
      })
      return acc
    }, [])

    //check if there any language exist errors
    if (errors.some((error) => error.code === 'exists')) {
      return 'Language code already exists'
    }

    //name
    if (errors.some((error) => error.name === 'exists')) {
      return 'Language name already exists'
    }

    //display name
    if (errors.some((error) => error.displayName === 'exists')) {
      return 'Display name already exists'
    }

    return 'Please fill all the fields'
  }

  // Handle saving the languages and passing to parent component
  const handleSaveLanguages = () => {
    if (!validateLanguages(languages)) return
    // Remove the error field from the languages object
    const list = languages.map(({ error, ...rest }) => rest)
    controller.addLanguages(list)
    onClose()
  }

  // Calculate if we need space for delete buttons
  const hasMultipleLanguages = languages.length > 1

  return (
    <Modal
      variant="admin"
      title="Add Languages"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape={true}
      className="md:absolute w-full sm:w-[600px] sm:max-w-[960px] h-full md:h-auto"
    >
      <div className="p-4">
        {/* Header row with equal width columns */}
        <div className="w-full text-admin_text grid grid-cols-12 gap-4 mb-2">
          {/* Using col-span-4 for each column to make them equal width */}
          <div className="flex items-center col-span-4 font-semibold">
            <Typography 
              variant="admin_body_accent"
              className="whitespace-nowrap my-0"
            >
              Language Code
            </Typography>

            <Tooltip 
              content="Language code like: en, es, fr..." 
              position="top"
            >
              <QuestionMarkCircleIcon className="w-5 h-5 ml-2 text-admin_text_secondary cursor-pointer" />
            </Tooltip>
          </div>

          <div className="col-span-4 font-semibold flex items-center">
            <Typography 
              variant="admin_body_accent"
              className="whitespace-nowrap"
            >
              Language Name
            </Typography>

            <Tooltip
              content="System name like: English, Spanish, French..."
              position="top"
            >
              <QuestionMarkCircleIcon className="w-5 h-5 ml-2 text-admin_text_secondary cursor-pointer" />
            </Tooltip>
          </div>

          <div className="col-span-4 font-semibold flex items-center">
            <Typography 
              variant="admin_body_accent"
              className="whitespace-nowrap"
            >
              Display Name
            </Typography>

            <Tooltip
              content="How the users will see it: English, Español, Français..."
              position="top"
            >
              <QuestionMarkCircleIcon className="w-5 h-5 ml-2 text-admin_text_secondary cursor-pointer" />
            </Tooltip>
          </div>
        </div>

        {/* Scrollable area with a fixed height */}
        <div className="w-full overflow-y-auto h-72">
          {languages.map((language, index) => (
            <div
              key={index}
              className="grid grid-cols-12 gap-4 mb-2 items-center"
            >
              {/* Input fields with consistent widths, adjusted for delete button */}
              <div className={`${hasMultipleLanguages ? 'col-span-4' : 'col-span-4'} flex items-center`}>
                <input
                  ref={index === 0 ? newInputRef : null}
                  type="text"
                  value={language.code}
                  onChange={(e) =>
                    handleLanguageChange(index, 'code', e.target.value)
                  }
                  className={`w-full p-2 rounded bg-admin_canvas focus:outline-none text-admin_text ${
                    hasError(language, 'code') ||
                    languageExistError(language, 'code')
                      ? 'border border-admin_error'
                      : 'border border-admin_neutral_1'
                  }`}
                /> 
              </div>

              <div className={`${hasMultipleLanguages ? 'col-span-4' : 'col-span-4'} flex items-center`}>
                <input
                  type="text"
                  value={language.name}
                  onChange={(e) =>
                    handleLanguageChange(index, 'name', e.target.value)
                  }
                  className={`w-full p-2 rounded bg-admin_canvas focus:outline-none text-admin_text ${
                    hasError(language, 'name') ||
                    languageExistError(language, 'name')
                      ? 'border border-admin_error'
                      : 'border border-admin_neutral_1'
                  }`}
                />
              </div>

              <div className={`${hasMultipleLanguages ? 'col-span-3' : 'col-span-4'} flex items-center`}>
                <input
                  type="text"
                  value={language.displayName}
                  onChange={(e) =>
                    handleLanguageChange(index, 'displayName', e.target.value)
                  }
                  className={`w-full p-2 rounded bg-admin_canvas focus:outline-none text-admin_text ${
                    hasError(language, 'displayName') ||
                    languageExistError(language, 'displayName')
                      ? 'border border-admin_error'
                      : 'border border-admin_neutral_1'
                  }`}
                />
              </div>

              {languages.length > 1 && (
                <div className="col-span-1 flex justify-center items-center">
                  <XMarkIcon
                    className="w-6 h-6 text-admin_text hover:text-admin_primary cursor-pointer"
                    onClick={() => handleRemoveLanguage(index)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Buttons bottom area */}
        <div className="flex justify-end mt-4 items-center">
          <div className="flex-grow justify-start">
            <span className="text-admin_error text mr-5">
              {checkForErrors ? getErrorNotification() : ''}
            </span>
          </div>

          {/* New Language button */}
          <Button
            variant="admin_secondary"
            className="gap-2 mr-6"
            onClick={handleAddNewLanguage}
          >
            New Language
            <PlusCircleIcon className="w-6 h-6 cursor-pointer" />
          </Button>

          <Button
            variant="admin_primary"
            className="w-full sm:w-32"
            onClick={handleSaveLanguages}
          >
            Add
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default LanguageAddDialog